import { v4 as uuidv4 } from "uuid";
import { getCookie, setCookie as _setCookie } from "cookies-next/client";

type IdentifyMeta = Record<string, any>;
type TrackProps = Record<string, any>;

const BEACON_URL = process.env["NEXT_PUBLIC_ANALYTICS_URL"];
const ANONYMOUS_ID_KEY = "ajs_anonymous_id";
const USER_ID_KEY = "ajs_user_id";
const USER_TRAITS_KEY = "ajs_user_traits";

const INVALID_VALUES = ["null", "undefined", "nan", "NaN"];

/**
 * Sets a cookie with specific configuration
 * @param name - Name of the cookie
 * @param value - Value to store in the cookie
 */
function setCookie(name: string, value: string) {
  _setCookie(name, value, {
    path: "/",
    expires: new Date(new Date().setFullYear(new Date().getFullYear() + 10)),
    sameSite: "lax",
    domain: process.env["NEXT_PUBLIC_COOKIE_DOMAIN"],
  });
}

/**
 * Retrieves current page information
 * @returns Object containing page path, referrer, search params, title and URL
 */
function getPageInfo() {
  return {
    path: window.location.pathname,
    referrer: document.referrer,
    search: window.location.search,
    title: document.title,
    url: window.location.href,
  };
}

/**
 * Sends analytics payload to the beacon URL
 * Falls back to fetch if sendBeacon is not available
 * @param data - The analytics payload to send
 */
function sendPayload(data: any) {
  const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
  const success = navigator.sendBeacon?.(BEACON_URL, blob);
  if (!success) {
    // Fallback
    fetch(BEACON_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  }
}

/**
 * Generates a unique message ID for analytics events
 * @returns A unique string identifier prefixed with 'ajs-next-'
 */
function generateMessageId(): string {
  const rand = crypto
    .getRandomValues(new Uint8Array(16))
    .reduce((acc, byte) => acc + byte.toString(16).padStart(2, "0"), "");
  return `ajs-next-${rand}`;
}

/**
 * Retrieves the anonymous ID from either cookie or localStorage
 * @returns The anonymous ID if exists, undefined otherwise
 */
function getAnonymousId(): string | undefined {
  let cookieAnonymousId = getCookie(ANONYMOUS_ID_KEY);
  let localStorageAnonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
  let anonymousId = cookieAnonymousId || localStorageAnonymousId;

  if (INVALID_VALUES.includes(anonymousId)) {
    return undefined;
  }

  return anonymousId;
}

/**
 * Retrieves the user ID from localStorage
 * @returns The user ID if exists, undefined otherwise
 */
function getUserId(): string | undefined {
  const user_id = localStorage.getItem(USER_ID_KEY);

  if (INVALID_VALUES.includes(user_id)) {
    return undefined;
  }

  return user_id;
}

/**
 * Cleans invalid values from localStorage
 * Removes user_id and anonymous_id if they are 'null' or 'undefined' strings
 */
function cleanLocalStorageValues() {
  const user_id = localStorage.getItem(USER_ID_KEY);
  if (!!user_id && ["null", "undefined"].includes(user_id)) {
    localStorage.removeItem(USER_ID_KEY);
  }

  const anonymous_id = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!!anonymous_id && ["null", "undefined"].includes(anonymous_id)) {
    localStorage.removeItem(ANONYMOUS_ID_KEY);
  }
}

/**
 * Analytics utility for tracking user behavior and events
 */
export const Analytics = {
  /**
   * Initializes the analytics system by managing anonymous user identification
   * Syncs anonymous ID between cookies and localStorage, creates new ID if none exists
   */
  init() {
    cleanLocalStorageValues();

    let cookieAnonymousId = getCookie(ANONYMOUS_ID_KEY);
    let localStorageAnonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);

    // if the cookie is there but the localstorage is not, sync
    if (!localStorageAnonymousId && !!cookieAnonymousId) {
      localStorage.setItem(ANONYMOUS_ID_KEY, cookieAnonymousId);
    }

    let anonymousId = cookieAnonymousId || localStorageAnonymousId;

    if (!anonymousId) {
      anonymousId = uuidv4();
      localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
      setCookie(ANONYMOUS_ID_KEY, anonymousId);
    }
  },

  /**
   * Identifies a user and stores their information
   * @param userId - Unique identifier for the user
   * @param meta - Additional user traits/metadata to store
   */
  identify(userId?: string | number, meta?: IdentifyMeta) {
    if (!userId) return;

    localStorage.setItem(USER_ID_KEY, userId + "");
    if (meta) {
      localStorage.setItem(USER_TRAITS_KEY, JSON.stringify(meta));
    }

    const payload = {
      timestamp: new Date().toISOString(),
      userId: getUserId(),
      anonymousId: getAnonymousId(),
      type: "identify",
      messageId: generateMessageId(),
      context: {
        page: getPageInfo(),
      },
      traits: meta,
      writeKey: process.env["NEXT_PUBLIC_ANALYTICS_KEY"],
    };

    sendPayload(payload);
  },

  /**
   * Tracks a custom event with optional properties
   * @param eventName - Name of the event to track
   * @param properties - Additional properties associated with the event
   */
  track(eventName: string, properties?: TrackProps) {
    const payload = {
      timestamp: new Date().toISOString(),
      userId: getUserId(),
      anonymousId: getAnonymousId(),
      type: "track",
      messageId: generateMessageId(),
      context: {
        page: getPageInfo(),
      },
      event: eventName,
      properties: properties || {},
      writeKey: process.env["NEXT_PUBLIC_ANALYTICS_KEY"],
    };

    sendPayload(payload);
  },

  reset() {
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(USER_TRAITS_KEY);
  },
};
